const API_URL: string = import.meta.env.VITE_API_URL || 'http://localhost:3000';

const CREDENTIALS_KEY = 'ruta-admin-insights-credentials';

export class UnauthorizedError extends Error {
  constructor() {
    super('Unauthorized');
    this.name = 'UnauthorizedError';
  }
}

export function getStoredCredentials(): string | null {
  return sessionStorage.getItem(CREDENTIALS_KEY);
}

export function storeCredentials(username: string, password: string): void {
  sessionStorage.setItem(CREDENTIALS_KEY, btoa(`${username}:${password}`));
}

export function clearCredentials(): void {
  sessionStorage.removeItem(CREDENTIALS_KEY);
}

export function isLoggedIn(): boolean {
  return getStoredCredentials() !== null;
}

async function request<T>(
  path: string,
  credentials: string,
  init?: { method?: string; body?: unknown },
): Promise<T> {
  const headers: Record<string, string> = { Authorization: `Basic ${credentials}` };
  if (init?.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${API_URL}${path}`, {
    method: init?.method ?? 'GET',
    headers,
    body: init?.body === undefined ? undefined : JSON.stringify(init.body),
  });

  if (response.status === 401) {
    throw new UnauthorizedError();
  }
  if (!response.ok) {
    const body = await response.text();
    let message = `Request failed (${response.status})`;
    try {
      const parsed = JSON.parse(body) as { error?: { message?: string } };
      if (parsed.error?.message) message = parsed.error.message;
    } catch {
      if (body) message = `${message}: ${body}`;
    }
    throw new Error(message);
  }

  const envelope = await response.json();
  return envelope.data as T;
}

/** GET a platform-operator endpoint using the stored credentials. */
export async function apiGet<T>(path: string): Promise<T> {
  const credentials = getStoredCredentials();
  if (!credentials) throw new UnauthorizedError();
  return request<T>(path, credentials);
}

/** PATCH a platform-operator endpoint using the stored credentials. */
export async function apiPatch<T>(path: string, body: unknown): Promise<T> {
  const credentials = getStoredCredentials();
  if (!credentials) throw new UnauthorizedError();
  return request<T>(path, credentials, { method: 'PATCH', body });
}

/** POST a platform-operator endpoint using the stored credentials. */
export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const credentials = getStoredCredentials();
  if (!credentials) throw new UnauthorizedError();
  return request<T>(path, credentials, { method: 'POST', body });
}

/** DELETE a platform-operator endpoint using the stored credentials. */
export async function apiDelete<T>(path: string): Promise<T> {
  const credentials = getStoredCredentials();
  if (!credentials) throw new UnauthorizedError();
  return request<T>(path, credentials, { method: 'DELETE' });
}

/**
 * Where the staff webapp lives, so a "view as" session can be handed off to it. Prefer an explicit
 * `VITE_WEBAPP_URL`; otherwise derive it from the API host (`*.api.getruta.com` → `*.app.getruta.com`).
 */
export function webappBaseUrl(): string {
  const explicit = import.meta.env.VITE_WEBAPP_URL as string | undefined;
  if (explicit) return explicit.replace(/\/$/, '');
  try {
    const u = new URL(API_URL);
    u.hostname = u.hostname.replace(/(^|\.)api\./, (_m, p1) => `${p1}app.`);
    return `${u.protocol}//${u.host}`;
  } catch {
    return 'https://app.getruta.com';
  }
}

export interface StaffSession {
  idToken: string;
  accessToken: string;
  expiresInSeconds: number;
  member: { id: string; name: string; roleId: string; orgId: string; orgName: string };
}

/**
 * Mint a "view as" staff session for `orgId` (defaults to the owner) and open the staff webapp's
 * session-injection route in a new tab. Tokens ride in the URL *hash*, which browsers never send to
 * the server and keep out of history/referrer — the webapp reads them, stores them, and scrubs the
 * hash immediately.
 */
export async function openViewAsSession(orgId: string, memberId?: string): Promise<void> {
  const session = await apiPost<StaffSession>(
    `/organizations/${orgId}/staff-session`,
    memberId ? { memberId } : {},
  );
  const params = new URLSearchParams({
    idToken: session.idToken,
    accessToken: session.accessToken,
    name: session.member.name,
    org: session.member.orgName,
  });
  window.open(`${webappBaseUrl()}/session#${params.toString()}`, '_blank', 'noopener');
}

/** Validate a candidate username/password against the session endpoint. */
export async function validateCredentials(username: string, password: string): Promise<{ environment: string }> {
  return request<{ ok: boolean; environment: string }>(
    '/platform-operator/session',
    btoa(`${username}:${password}`),
  );
}
